import { defineStore } from 'pinia'
import { fetchOrgsApi, createOrgApi, updateOrgApi, deleteOrgApi } from '@/datasources/api/orgs'
import type { Organization } from '@/types'

interface OrgState {
  orgs: Organization[]
  activeOrgId: string | null
  loading: boolean
}

export const useOrgStore = defineStore('org', {
  state: (): OrgState => ({
    orgs: [],
    activeOrgId: localStorage.getItem('si-active-org'),
    loading: false,
  }),
  getters: {
    activeOrg: (state): Organization | null =>
      state.orgs.find((o) => o.id === state.activeOrgId) ?? null,
  },
  actions: {
    async fetchOrgs() {
      this.loading = true
      try {
        const { data } = await fetchOrgsApi()
        const orgs = Array.isArray(data) ? data : []
        this.orgs = orgs
        // A stale si-active-org (e.g. from a previous DB reset) may point at an
        // org that no longer exists — never let the API layer filter everything
        // out via an invalid org_id. Reset to the first valid org in that case.
        const activeStillValid =
          this.activeOrgId != null && orgs.some((o) => o.id === this.activeOrgId)
        if (!activeStillValid && orgs.length > 0) {
          this.activeOrgId = orgs[0].id
          localStorage.setItem('si-active-org', orgs[0].id)
        }
      } catch {
        this.orgs = []
      } finally {
        this.loading = false
      }
    },
    async createOrg(name: string) {
      const res = await createOrgApi(name)
      const org = res.data
      this.orgs.push(org)
      // Switch before returning so the axios interceptor tags the caller's
      // next request (e.g. createAppApi) with the new org_id.
      this.switchOrg(org.id)
      return org
    },
    async updateOrg(orgId: string, data: { name?: string }) {
      const res = await updateOrgApi(orgId, data)
      const idx = this.orgs.findIndex((o) => o.id === orgId)
      if (idx !== -1) this.orgs[idx] = res.data
    },
    async deleteOrg(orgId: string) {
      await deleteOrgApi(orgId)
      this.orgs = this.orgs.filter((o) => o.id !== orgId)
      if (this.activeOrgId === orgId) {
        this.activeOrgId = this.orgs[0]?.id ?? null
        if (this.activeOrgId) localStorage.setItem('si-active-org', this.activeOrgId)
        else localStorage.removeItem('si-active-org')
      }
    },
    switchOrg(orgId: string) {
      this.activeOrgId = orgId
      localStorage.setItem('si-active-org', orgId)
    },
  },
})
